import { useState, useCallback } from 'react';
import QueryForm, { QueryFormData } from '../components/QueryForm';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { queryService, QuerySubmissionData } from '../services/queryService';
import { SubmissionResponse } from '../types';
import './QueryPage.css';

function QueryPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successResponse, setSuccessResponse] = useState<SubmissionResponse | null>(null);

  // Handle query form submission
  const handleSubmit = useCallback(async (formData: QueryFormData) => {
    setLoading(true);
    setError(null);
    setSuccessResponse(null);

    try {
      // Prepare data for API (remove empty fields)
      const queryData: QuerySubmissionData = {
        requestId: formData.requestId?.trim() || undefined,
        productName: formData.productName?.trim() || undefined,
        email: formData.email.trim(),
        queryType: formData.queryType,
        message: formData.message.trim(),
      };

      const response = await queryService.submitQuery(queryData);
      setSuccessResponse(response);
    } catch (err) {
      // Handle error - display user-friendly message without stack traces (Requirement 12.1)
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Unable to submit your query. Please check your internet connection and try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle retry on error
  const handleRetry = useCallback(() => {
    setError(null);
    setSuccessResponse(null);
  }, []);

  // Check if we should show the form (not in success state)
  const showForm = !successResponse;

  return (
    <div className="query-page">
      <div className="query-page__container">
        <header className="query-page__header">
          <h1>Query Submission</h1>
          <p className="query-page__description">
            Check the status of your COA request or submit an inquiry. Fill out the form and our team will respond shortly.
          </p>
        </header>

        {loading && (
          <div className="query-page__loading" role="status" aria-live="polite">
            <LoadingSpinner 
              size="large" 
              label="Submitting your query..." 
            />
            <p className="query-page__loading-text">Please wait while we submit your query.</p>
          </div>
        )}

        {error && !loading && (
          <section className="query-page__error-section" aria-label="Error message">
            <ErrorMessage 
              message={error} 
              onRetry={handleRetry}
              className="query-page__error"
            />
          </section>
        )}

        {successResponse && (
          <section className="query-page__success" aria-label="Success message" role="status">
            <div className="query-page__success-icon" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2 className="query-page__success-title">Query Submitted Successfully</h2>
            <p className="query-page__success-message">
              Your query has been submitted. We will review it and get back to you shortly.
            </p>
            <div className="query-page__success-details">
              <div className="query-page__success-detail">
                <span className="query-page__success-label">Query ID:</span>
                <span className="query-page__success-value">{successResponse.requestId}</span>
              </div>
              <div className="query-page__success-detail">
                <span className="query-page__success-label">Submitted:</span>
                <span className="query-page__success-value">
                  {new Date(successResponse.submittedAt).toLocaleString()}
                </span>
              </div>
            </div>
            <button 
              className="query-page__new-query-btn"
              onClick={() => setSuccessResponse(null)}
            >
              Submit Another Query
            </button>
          </section>
        )}

        {showForm && !loading && (
          <section className="query-page__form-section" aria-label="Query form">
            <QueryForm 
              onSubmit={handleSubmit}
              loading={loading}
            />
          </section>
        )}
      </div>
    </div>
  );
}

export default QueryPage;